// Firebase Cloud Messaging (web push) integration. All config comes from
// REACT_APP_FIREBASE_* env vars; nothing here is secret (it is public client
// config). Push is a progressive enhancement: every entry point degrades
// gracefully when the browser is unsupported or config is missing.
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';

export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY ?? '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ?? '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID ?? 'kivo-skin',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ?? '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID ?? '',
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY ?? '';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

/** True only when the browser supports FCM and the required config is present. */
export async function pushSupported(): Promise<boolean> {
  if (!firebaseConfig.messagingSenderId || !VAPID_KEY) return false;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

function getApp(): FirebaseApp {
  if (!app) {
    const apps = getApps();
    app = apps.length > 0 ? apps[0] : initializeApp(firebaseConfig);
  }
  return app;
}

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!(await pushSupported())) return null;
  messaging ??= getMessaging(getApp());
  return messaging;
}

/**
 * Register the FCM service worker, passing the public config as query params so
 * the static worker file stays env-driven.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  });
  return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`);
}

/**
 * Request (or read) the FCM registration token for this device. Assumes
 * Notification permission has already been granted by the caller.
 */
export async function requestPushToken(): Promise<string | null> {
  const m = await getMessagingInstance();
  if (!m) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  try {
    const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    return token ? token : null;
  } catch {
    return null;
  }
}

/** Subscribe to foreground messages (tab focused). Returns an unsubscribe fn. */
export async function onForegroundMessage(
  cb: (payload: MessagePayload) => void
): Promise<() => void> {
  const m = await getMessagingInstance();
  if (!m) return () => { /* unsupported */ };
  return onMessage(m, cb);
}

/** Current browser Notification permission, or 'unsupported'. */
export function currentPermission(): NotificationPermission | 'unsupported' {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}
